import React, { useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { produce } from 'immer';
import { FunnelPlacementContext } from '@bbkAdminComponents/experiences/offers/funnel-placement-context';
import {
  GenericSelect,
  type GroupedMultiSelectOptions,
  type SelectOption,
} from '@bbkAdminComponents/select/select';
import { selectAppId } from '@bbkAdminRedux/app/selectors';
import { useSelectAllOffers } from '@bbkAdminRedux/rtkq/offers-and-lossAversions.slice';
import { createOfferCardpackPlacement } from '@bbkAdminRedux/rtkq/placements.slice';
import {
  isPlacementForOfferCardpack,
  PlacementOfferCardpack,
} from '@bbkAdminRedux/rtkq/placements.utils';
import { OfferCategory } from '@bbkAdminUtils/api-client/rest-offers-api';
import { GlobalLogger } from '@bbkAdminUtils/GlobalLogger';
import { FunnelContext } from '../FunnelContext';

export const OfferCardpackSelector: React.FC<{
  setOfferPackId?: (id: string | undefined) => void;
}> = ({ setOfferPackId }) => {
  const appKey = useSelector(selectAppId);
  const { funnelProps } = useContext(FunnelContext);
  const funnelId = +funnelProps!.funnelId;
  const { funnelPlacement, setFunnelPlacement } = useContext(
    FunnelPlacementContext
  );

  const defaultValue = {
    value: undefined,
    label: 'Select an Offer Set',
  };

  const allOffers = useSelectAllOffers({ appKey });
  const offerPacks = allOffers?.filter(
    (o) => o.category === OfferCategory.offer_pack
  );

  const offerPackOptions: GroupedMultiSelectOptions<
    SelectOption<string | undefined>
  > = [
    {
      value: undefined,
      label: 'Select an Offer Set',
    },
    ...offerPacks.map((o) => {
      return {
        value: o.id,
        label: o.display_name,
      };
    }),
  ];

  const onPlacementChange = (laOpt?: { value: string | undefined }) => {
    if (!funnelPlacement) return;

    const updatedPlacement = produce(funnelPlacement, (draft) => {
      if (!draft) return;
      let draftOfferCardpackPlacement = draft.placements
        .filter(isPlacementForOfferCardpack)
        .find((p) => p.position === PlacementOfferCardpack.offer_pack_center);

      if (!draftOfferCardpackPlacement) {
        GlobalLogger.warn(
          'Could not find offer card pack placement ',
          funnelPlacement
        );
        draftOfferCardpackPlacement = createOfferCardpackPlacement({
          position: PlacementOfferCardpack.offer_pack_center,
          funnel_id: funnelId,
        });
        draft.placements.push(draftOfferCardpackPlacement);
      }

      draftOfferCardpackPlacement.content_id = laOpt?.value;
    });
    setFunnelPlacement(updatedPlacement);
  };

  const selectedValue = useMemo(() => {
    let draftOfferCardpackPlacement = funnelPlacement.placements
      .filter(isPlacementForOfferCardpack)
      .find((p) => p.position === PlacementOfferCardpack.offer_pack_center);

    if (!draftOfferCardpackPlacement) return undefined;

    const selectedOfferPack = offerPacks.find(
      (o) => o.id === draftOfferCardpackPlacement.content_id
    );
    if (!selectedOfferPack) return undefined;
    setOfferPackId?.(selectedOfferPack.id);
    return {
      value: selectedOfferPack.id,
      label: selectedOfferPack.display_name,
    };
  }, [funnelPlacement, offerPacks]);

  return (
    <GenericSelect
      menuPosition="fixed"
      isSearchable={false}
      options={offerPackOptions}
      value={selectedValue ?? defaultValue}
      placeholder="Select an Offer card pack"
      noOptionsMessage={() => 'No Offer card pack'}
      onChange={(opt) => onPlacementChange(opt)}
    />
  );
};
